import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Utils */
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import {
  getDealerFeesTotal,
  getSortedDealerFees,
  getDealerFeesTotalDisclaimer,
  hasNoDealerFees,
} from 'site-modules/shared/utils/inventory/vdp-utils';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
/* Components */
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { PricingBlock } from 'site-modules/shared/components/inventory/pricing-block/pricing-block';

import './dealer-fees-price-details.scss';

export function DealerFeesPriceDetails({ vinData = {}, innerClassName = 'px-1_5 py-1', isLeaseView = false }) {
  const prices = get(vinData, 'prices');

  const isNewVehicle = isNew(vinData?.type);
  const dealerMsrp = get(prices, 'dealerMsrp', 0);
  const advertisedPrice = get(prices, 'advertisedPrice', 0);
  const dealerFees = get(prices, 'dealerFees', []);
  const sortedFees = getSortedDealerFees(dealerFees, isLeaseView, vinData);
  const has0PriceFee = sortedFees.some(fee => fee.price === 0);
  const feesTotal = getDealerFeesTotal(sortedFees);
  const basePrice = advertisedPrice - feesTotal;

  const basePriceText = has0PriceFee ? 'Price' : 'Base Price';
  const msrpSubcopy = 'This is the suggested retail price of the vehicle, including manufacturer-equipped options.';

  const totalDisclaimer = getDealerFeesTotalDisclaimer(vinData);
  const isMsrpDisplayed = isNewVehicle && !!dealerMsrp;

  const isNoDealerFees = hasNoDealerFees(vinData);

  return (
    <div className="dealer-fees-price-details text-gray-darker">
      <div className={classnames('price-details-block bg-white', innerClassName)}>
        {isMsrpDisplayed && (
          <PricingBlock
            title={<h3 className="mb-0_5 fw-bold size-16">{`MSRP: ${formatPriceStringV2(dealerMsrp)}`}</h3>}
            subcopy={msrpSubcopy}
            price=""
            priceClasses="fw-bold"
            priceBlockClasses="py-0_5 justify-content-start"
          />
        )}
        <PricingBlock
          title={<h3 className={classnames('mb-0 size-16', { 'fw-normal': isMsrpDisplayed })}>{basePriceText}</h3>}
          price={basePrice}
          priceClasses={classnames({ 'fw-bold': !isMsrpDisplayed })}
          priceBlockClasses="py-0_5"
        />

        {isNoDealerFees && (
          <PricingBlock
            title={<h3 className="fw-normal mb-0 size-16">Dealer Fees</h3>}
            price={0}
            priceBlockClasses="py-0_5"
          />
        )}

        {sortedFees.map(fee => (
          <PricingBlock
            key={fee.name}
            title={<h3 className="fw-normal mb-0 size-16">{fee.name}</h3>}
            price={fee.price || NaN}
            pricePrefix="+"
            priceBlockClasses="py-0_5"
          />
        ))}
        <div className="pricing-block-separator mt-0_5" />
        <PricingBlock
          title={<h3 className="fw-bold size-16">Total Price</h3>}
          price={advertisedPrice}
          priceClasses="fw-bold size-20"
          priceBlockClasses="py-0_5 pt-0_75"
          subcopy={<ContentFragment>{totalDisclaimer}</ContentFragment>}
        />
      </div>
    </div>
  );
}

DealerFeesPriceDetails.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  innerClassName: PropTypes.string,
  isLeaseView: PropTypes.bool,
};
